/* -----------------Globals--------------- */
import { useCallback, useEffect, useState } from "react";
import { Appbar, Surface } from "react-native-paper";

import DialogSession from "./SessionDialog";
import SessionEndDialog from "./SessionEndDialog";
/* -----------------Child components--------------- */
import StudySessionAppBar from "./StudySessionAppBar";

import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
/* -----------------Helpers & Hooks--------------- */
import { router, useFocusEffect } from "expo-router";
import { useAilaStore } from "../hooks/useAilaStore";
import useEditorStore from "../hooks/useEditorStore";
import { usePreventBackNavigation } from "../hooks/usePreventBackNavigation";
import useSessionStore from "../hooks/useSessionStore";
import useCountDown from "../hooks/useTimer";

/* -----------------Constants & Types--------------- */
import { events } from "@constants/tracking";
import { ConfirmDialog } from "@memorang/ui/src/components/ConfirmDialog";
import type { ActionType } from "./StudySessionAppBar/StudySessionActionsFab";

import { isWeb } from "@helpers/platform";
/* -----------------Utils--------------- */
import { trackCustomEvent } from "analytics";

import Icon from "@expo/vector-icons/MaterialCommunityIcons.js";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { handleNavigateBack } from "@helpers/common";
import { useAppStore } from "@hooks/useAppStore";
import useInvalidateQueries from "@hooks/useInvalidateQueries";
import { getPlatform } from "@memorang/helpers/src/common";
/* -----------------UI Components--------------- */
import Box from "@memorang/ui/src/components/Box";
import Button from "@memorang/ui/src/components/Button";
import FlightCalculatorDialog from "components/FlightCalculator/FlightCalculatorDialog";
import { trackAnswerEvent } from "../api";
import type { TrackAnswerEventRequest } from "../api/types";
import { getExitDialogConfig, helpDialog } from "../constants/dialog-configs";
import useCurrentSession from "../hooks/useCurrentSession";
import { useSession } from "../hooks/useSession";

const TIMEOUT_DURATION_AUTO_REDIRECT = 2000;
export const StudySessionAppBarContainer = ({
	showStartDialog,
}: {
	showStartDialog?: boolean;
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}) => {
	const sessionNumericId = useSessionStore((state) => state.numericId);
	const sessionItems = useSessionStore((state) => state.sessionItems);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const totalTime = useSessionStore((state) => state.totalTime);
	const updateTimeElapsed = useSessionStore((state) => state.updateTimeElapsed);
	const timeElapsed = useSessionStore((state) => state.timeElapsed);
	const blockStates = useSessionStore((state) => state.blockStates);
	const itemStates = useSessionStore((state) => state.itemStates);
	const startTime = useSessionStore((state) => state.startTime);
	const serverExamBreak = useSessionStore((state) => state.serverExamBreak);
	const examName = useSessionStore((state) => state.examName);
	const sessionSubType = useSessionStore((state) => state.sessionSubType);
	const answers = useSessionStore((state) => state.answers);
	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);
	const [showFlightCalculator, setShowFlightCalculator] = useState(false);
	const updateMarkedItemsForReview = useSessionStore(
		(state) => state.updateMarkedItemsForReview,
	);

	const shouldShowFlightCalculator = useAppStore(
		(state) => state.sessionView?.showFlightCalculator,
	);
	const sessionId = useSessionStore((state) => state.id);
	const sessionTime = useSessionStore((state) => state.sessionTime);

	const isImageChallengeApp = useAppStore(
		(state) => state.app.isImageChallengeApp,
	);

	const { endCurrentSession } = useSession();

	const [showTimeExpiredDialog, setShowTimeExpiredDialog] = useState(false);

	const toggleShowTimeExpiredDialog = useCallback(() => {
		setShowTimeExpiredDialog((prev) => !prev);
	}, []);

	const isOlxSession = examName?.includes("OLX");

	const editorRef = useEditorStore((store) => store.editorRef);
	const [hasFocus, setHasFocus] = useState(false);

	const breaks = useSessionStore((state) => state.breaks);

	const isDiagnostic = useSessionStore((state) => state.isDiagnostic);
	const sessionType = useSessionStore((state) => state.sessionType);
	const collectionId = useSessionStore((state) => state.collectionId);

	const lastBreak = breaks?.length
		? breaks[breaks.length - 1]
		: serverExamBreak;

	const breakTimeTakenInSeconds = lastBreak?.totalBreakTimeTakenInSeconds;

	const { isMobile, isMediumWindowSize } = useDevice();
	const hasAnsweredAtLeastOneItem = answers ? answers.size > 0 : false;
	const isBreakOngoing = useSessionStore((state) => state.isBreakOngoing);

	const calculateTotalTimeRemaining = () => {
		if (isOlxSession && startTime && !isBreakOngoing) {
			const currentTime = Date.now();
			let timeElapsed = Math.floor(
				(currentTime - new Date(startTime).getTime()) / 1000,
			);
			if (breakTimeTakenInSeconds) {
				timeElapsed -= breakTimeTakenInSeconds;
			}

			return Math.max(0, totalTime - timeElapsed);
		}
		return totalTime - (sessionTime || 0);
	};

	const totalTimeRemaining = calculateTotalTimeRemaining();

	const isTimeExpired = totalTimeRemaining <= 0;

	useFocusEffect(
		useCallback(() => {
			setHasFocus(true);
			return () => {
				setHasFocus(false);
			};
		}, []),
	);

	const { invalidateAllQueries } = useInvalidateQueries();

	const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
	const [showSummativeEndSessionDialog, setShowSummativeEndSessionDialog] =
		useState(false);
	const [showBrowserBackWarning, setShowBrowserBackWarning] = useState(false);
	const updateShowDrawer = useAilaStore((state) => state.updateShowDrawer);

	const toggleShowSummativeEndSessionDialog = () => {
		setShowSummativeEndSessionDialog((prev) => !prev);
	};

	const toggeShowEndSessionDialog = useCallback(() => {
		if (inReviewMode) {
			router.back();
			return;
		}
		setShowEndSessionDialog((prev) => !prev);
		return true;
	}, [inReviewMode]);

	const handleBackPress = useCallback(() => {
		setShowBrowserBackWarning(true);
	}, []);

	const { allowNavigation } = usePreventBackNavigation({
		enabled: !inReviewMode,
		onBackPress: handleBackPress,
		hasFocus,
	});

	useEffect(() => {
		if (!isSummativeTest && isWeb) {
			window.onbeforeunload = () => {
				return "";
			};

			return () => {
				window.onbeforeunload = null;
			};
		}
	}, [isSummativeTest]);

	const handleCancelExit = useCallback(() => {
		setShowBrowserBackWarning(false);
	}, []);

	const handleConfirmExit = useCallback(() => {
		setShowBrowserBackWarning(false);
		allowNavigation();
		invalidateAllQueries();
		handleNavigateBack("/(protected)/(tabs)/home");
	}, [allowNavigation, invalidateAllQueries]);

	const {
		currentItem,
		currentBlockIndex,
		currentItemIndex,
		currentBlock,
		numTotalItems,
		numSessionItems: numSections,
	} = useCurrentSession();

	const currentItemId = currentItem?.id;

	const currentItemState = itemStates?.get(currentItemId!);

	const choices =
		currentItem.response.type === "choice" ? currentItem.response.choices : [];
	const numItemsLeft = currentBlock.filter(
		(item) => !itemStates?.get(item.id!)?.answered,
	).length;
	const isAnswered = currentItemState?.answered;

	const currentBlockState = blockStates.get(currentBlockIndex);
	// const currentBlockProgress = currentBlockState?.currentProgress || 0;
	const markedItems = currentBlockState?.markedItemsForReview;
	const highlightedItems = currentBlockState?.highlightedItems;
	const isMarked =
		currentItem?.answer?.marked || markedItems?.includes(currentItemId!);
	const highlightedHtml =
		currentItem?.answer?.highlight || highlightedItems?.get(currentItemId!);

	const { isNegative, hours, minutes, seconds, pause, resume } = useCountDown(
		(totalTimeRemaining || 0) * 1000,
		isSummativeTest && !inReviewMode && !isBreakOngoing,
	);

	const {
		currentExamContext: { projectId, organizationId },
	} = useExamContext();

	useEffect(() => {
		if (isSummativeTest) {
			if (isBreakOngoing || showStartDialog) {
				pause();
			} else {
				resume();
			}
		}
	}, [isBreakOngoing, pause, resume, isSummativeTest, showStartDialog]);

	const formattedTime = `${
		isNegative ? "-" : ""
	}${hours}:${minutes}:${seconds}`;

	const hasParentStem = currentItem?.recordType === "SCENARIO" && !isMobile;

	const constructTrackAnswerEventRequest = useCallback(
		(sessionTime: number) => {
			const trackAnswerEventRequest: TrackAnswerEventRequest = {
				itemId: currentItemId!,
				itemVersionId: currentItem.versionId || 0,
				sessionId,
				collectionId,
				projectId,
				organizationId,
				sessionType: "SUMMATIVE",
				itemDetails: {
					type: "ANSWER_EVENT",
					itemType: currentItem.metadata?.type!,
					variant: currentItem.metadata?.type!,
				},
				response: {
					selectedChoiceIds:
						currentItem.answer?.selectedChoiceIds ||
						currentItemState?.selectedChoiceIds ||
						[],
				},
				performance: {},
				settings: {},
				userInteraction: {
					highlight: highlightedHtml!,
					marked: isMarked!,
					sessionTime,
				},
				platform: getPlatform(),
			};
			return trackAnswerEventRequest;
		},
		[
			currentItemId,
			currentItem.versionId,
			sessionId,
			collectionId,
			projectId,
			organizationId,
			currentItemState?.selectedChoiceIds,
			currentItem.answer?.selectedChoiceIds,
			highlightedHtml,
			isMarked,
			currentItem.metadata?.type,
		],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		const handleTimeExpired = async () => {
			if (isOlxSession && isTimeExpired) {
				trackCustomEvent({
					eventName: events.sessionExpired,
				});
				const trackAnswerEventRequest =
					constructTrackAnswerEventRequest(totalTime);
				await trackAnswerEvent(trackAnswerEventRequest);
				toggleShowTimeExpiredDialog();
				timeoutId = setTimeout(() => {
					toggleShowTimeExpiredDialog();
					endCurrentSession({
						id: sessionId,
					});
					router.replace({
						pathname: "/(protected)/session/[id]/report",
						params: {
							id: sessionNumericId,
						},
					});
				}, TIMEOUT_DURATION_AUTO_REDIRECT);
			}
		};

		handleTimeExpired();

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [
		isOlxSession,
		isTimeExpired,
		sessionId,
		sessionNumericId,
		toggleShowTimeExpiredDialog,
		constructTrackAnswerEventRequest,
		totalTime,
	]);

	const handleUpdateItemStateForTime = useCallback(async () => {
		trackCustomEvent({
			eventName: events.sessionPaused,
		});
		const trackAnswerEventRequest = constructTrackAnswerEventRequest(
			timeElapsed + (sessionTime || 0),
		);

		await trackAnswerEvent(trackAnswerEventRequest);
		router.replace("/practice-tests");
	}, [constructTrackAnswerEventRequest, sessionTime, timeElapsed]);

	useEffect(() => {
		let interval: ReturnType<typeof setInterval> | null = null;
		interval = setInterval(() => {
			if (!inReviewMode && isSummativeTest && !isBreakOngoing) {
				updateTimeElapsed(timeElapsed + 1);
			}
		}, 1000);
		if (isBreakOngoing) {
			clearInterval(interval);
		}
		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [
		inReviewMode,
		isSummativeTest,
		timeElapsed,
		updateTimeElapsed,
		isBreakOngoing,
	]);

	const handleNavigation = (type: "positive" | "negative") => {
		toggeShowEndSessionDialog();

		if (type === "positive") {
			updateShowDrawer(false);
			if (hasAnsweredAtLeastOneItem) {
				endCurrentSession({
					id: sessionId,
				});
				router.replace({
					pathname: "/(protected)/session/[id]/report",
					params: {
						id: sessionId,
					},
				});
			} else {
				router.back();
			}
		}
	};
	const handleClickRightCta = () => {
		if (isSummativeTest) {
			if (inReviewMode) {
				updateShowDrawer(false);
				router.back();
			} else {
				toggleShowSummativeEndSessionDialog();
			}
		} else {
			toggeShowEndSessionDialog();
		}
	};

	const handleClickButton = (type: string) => {
		if (type === "confirm") {
			invalidateAllQueries();
			handleUpdateItemStateForTime();
		}
		toggleShowSummativeEndSessionDialog();
	};

	const toggleMark = () => {
		if (!inReviewMode) {
			updateMarkedItemsForReview(currentItemId!);
		}
	};
	const handleAction = (type: ActionType) => {
		switch (type) {
			case "pause":
				handleClickRightCta();
				break;
			case "mark":
				toggleMark();
				break;
			default:
				break;
		}
	};

	const theme = useAppTheme();

	const markActionIcon = isMarked ? "flag" : "flag-variant-outline";
	const markActionLabel = isMarked ? "Marked" : "Mark";
	const markActionColor = isMarked
		? theme.palette.error.main
		: theme.colors.white;

	const showToolbar = inReviewMode ? isMarked : !isMobile && isSummativeTest;

	const handleClickMenuItem = (type: "pause" | "help") => {
		switch (type) {
			case "pause":
				toggleShowSummativeEndSessionDialog();
				break;
			case "help":
				{
					const confirmDialogConfig = helpDialog({
						sessionItems: currentBlock,
						numSections,
						examName: examName || "",
						totalTime,
						isDiagnosticTest: sessionSubType === "DIAGNOSTIC",
						numTotalQuestions: numTotalItems,
						diagnosticLabel,
					});
					router.push({
						pathname: "/info",
						params: {
							title: confirmDialogConfig.title,
							body: confirmDialogConfig.body,
						},
					});
				}
				break;
			default:
				break;
		}
	};

	const handleHighlight = () => {
		if (editorRef) {
			if (editorRef.isActive("highlight")) {
				editorRef.commands.unsetHighlight();
				return;
			}
			editorRef.commands.setHighlight();
		}
	};
	const isSingleItemSession = sessionItems.length === 1;

	if (isSingleItemSession && isImageChallengeApp) {
		return (
			<Appbar.Header mode="small">
				<Appbar.Action icon="close" onPress={() => router.back()} />
				<Appbar.Content title={`Challenge #${currentItemId}`} />
				<Box flexDirection="row" gap={4} paddingRight={8}>
					{choices.map((_, index) => (
						<Icon
							key={`${index}-${currentItemId}`}
							name={index < 3 ? "heart-outline" : "heart"}
							size={isMediumWindowSize ? 24 : 16}
							color={theme.palette.error.dark}
						/>
					))}
				</Box>
			</Appbar.Header>
		);
	}

	const exitDialogCopy = sessionType
		? getExitDialogConfig({ isOlx: isOlxSession })[sessionType]
		: undefined;
	return (
		<>
			<Box
				accessibilityLabel={`Study Session App Bar, containing the exam name - ${examName}, question number - ${currentItemIndex + 1}, time remaining - ${formattedTime}, and toolbar with pause, highlight, and mark buttons`}
				role="banner"
			>
				<StudySessionAppBar
					numericId={sessionNumericId}
					examName={examName}
					timeRemaining={formattedTime}
					isTimerExpired={isNegative}
					handleClickRightCta={handleClickRightCta}
					showPause={!isOlxSession}
					isAnswered={isAnswered}
					hasParentStem={Boolean(hasParentStem)}
					handleClickMenuItem={handleClickMenuItem}
				/>
				{showToolbar && (
					<Surface
						style={{
							backgroundColor: theme.colors.info.main,
						}}
					>
						<Box
							justifyContent="space-between"
							flexDirection="row"
							padding={16}
						>
							{inReviewMode ? (
								<Box />
							) : (
								<Box flexDirection="row" gap={8} alignItems="center">
									{shouldShowFlightCalculator && (
										<Button
											icon={"calculator"}
											textColor={theme.colors.white}
											onPress={() => setShowFlightCalculator(true)}
										>
											Calculator
										</Button>
									)}
									<Button
										textColor={theme.colors.white}
										icon={"marker"}
										onPress={handleHighlight}
									>
										Highlight
									</Button>
								</Box>
							)}
							<Button
								onPress={() => {
									if (!inReviewMode) {
										handleAction("mark");
									}
								}}
								icon={markActionIcon}
								textColor={markActionColor}
								style={{
									backgroundColor: isMarked ? theme.colors.white : undefined,
								}}
							>
								{markActionLabel}
							</Button>
						</Box>
					</Surface>
				)}
			</Box>
			{showEndSessionDialog && (
				<SessionEndDialog
					visible={showEndSessionDialog}
					onDismiss={toggeShowEndSessionDialog}
					handleAction={handleNavigation}
					hasAnsweredAtLeastOneItem={hasAnsweredAtLeastOneItem}
					isDiagnostic={isDiagnostic}
					numItemsLeft={numItemsLeft}
				/>
			)}
			{showSummativeEndSessionDialog && (
				<DialogSession
					type="pause"
					open={showSummativeEndSessionDialog}
					handleClose={toggleShowSummativeEndSessionDialog}
					handleClickButton={handleClickButton}
				/>
			)}
			{showTimeExpiredDialog && (
				<DialogSession
					type="timerExpired"
					maxWidth={"xs"}
					open={showTimeExpiredDialog}
					showCloseButton={false}
					handleClose={toggleShowTimeExpiredDialog}
				/>
			)}
			{showFlightCalculator && (
				<FlightCalculatorDialog
					dialogVisible={showFlightCalculator}
					hideDialog={() => setShowFlightCalculator(false)}
				/>
			)}
			{showBrowserBackWarning && exitDialogCopy && (
				<ConfirmDialog
					visible={showBrowserBackWarning}
					title={exitDialogCopy.title}
					icon="alert-circle-outline"
					body={exitDialogCopy.body}
					dismissCallback={handleConfirmExit}
					dismissCta={exitDialogCopy.dismissCta}
					confirmCallback={handleCancelExit}
					confirmCta={exitDialogCopy.confirmCta}
					maxWidth="xs"
				/>
			)}
		</>
	);
};
